// src/components/Navbar.jsx

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
// ✨ 1. Make sure all icons are imported
import { 
  FaUserCircle, FaAngleDown, FaSignOutAlt, FaTachometerAlt, FaUsersCog, 
  FaBars, FaTimes, FaArchive, FaBookOpen, FaCogs, FaEnvelope, FaWhatsapp, FaVideo, FaCalendarAlt 
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import NotificationBell from "./NotificationBell";
import "./Navbar.css";

const Navbar = () => {
  const { currentUser, userData, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const userDropdownRef = useRef(null);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ✨ 2. Add state for the marketing sub-dropdown
  const [marketingDropdownOpen, setMarketingDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✨ 3. Create a helper to close all menus
  const closeAllMenus = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMarketingDropdownOpen(false); // Also reset the sub-dropdown
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    closeAllMenus(); // Use the new helper
  };

  const handleCompanyClick = () => {
    if (!userData) {
      navigate("/login");
      return;
    }
    if (userData.role === 'superadmin' || userData.role === 'admin') {
      navigate("/dashboard/manage");
    } else {
      navigate("/dashboard/my-portal");
    }
    closeAllMenus();
  };

  const hasPersonalDashboard = userData?.role === 'admin' || userData?.role === 'employee';
  const hasManagementPortal = userData?.role === 'admin' || userData?.role === 'superadmin';
  const isSuperAdmin = userData?.role === 'superadmin';
  const isLoggedIn = !!userData;

  const userMenuContent = (
    <>
      {/* Identity card header */}
      <div className="user-menu-header">
        <img
          src={userData?.photoURL || '/default-avatar.png'}
          alt="avatar"
          className="user-menu-avatar"
        />
        <div className="user-menu-info">
          <span className="user-menu-name">{userData?.name}</span>
          <span className="user-menu-email">{userData?.email}</span>
          <span className={`user-menu-role-pill user-menu-role-pill--${userData?.role}`}>
            {userData?.role}
          </span>
        </div>
      </div>
      <div className="user-menu-divider" />
      <li onClick={handleLogout} className="logout-link">
        <FaSignOutAlt /> Logout
      </li>
    </>
  );

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-brand" onClick={handleCompanyClick}>
          <div className="brand-box">
            <span className="brand-name">
              <span className="brand-blue">PrrepX</span> <span className="brand-orange">Pathshala</span>
            </span>
          </div>
          <span className="brand-tagline">Employee Portal</span>
        </div>

        <div className="navbar-right">
          <div className="desktop-controls">
            {currentUser && userData ? (
              <>
                  <div className="user-dropdown-wrapper" ref={userDropdownRef}>
                <button className="user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                  <img src={userData.photoURL || '/default-avatar.png'} alt="avatar" className="user-avatar-sm" />
                  <div className="user-btn-text">
                    <span className="user-name">{userData.name}</span>
                    <span className={`user-btn-role user-btn-role--${userData.role}`}>{userData.role}</span>
                  </div>
                  <FaAngleDown className={`dropdown-arrow ${dropdownOpen ? "rotated" : ""}`} />
                </button>
                {dropdownOpen && (
                  <ul className="user-dropdown-menu">{userMenuContent}</ul>
                )}
                </div>
                <Link to="/dashboard/meet" className="navbar-meet-btn" title="PrrepX Meet" onClick={() => setDropdownOpen(false)}>
                  <FaVideo size={14} />
                  <span>PrrepX Meet</span>
                </Link>
                <Link to="/dashboard/calendar" className="navbar-meet-btn" title="Calendar" onClick={() => setDropdownOpen(false)}>
                  <FaCalendarAlt size={14} />
                </Link>
                <NotificationBell />
              </>
            ) : (
              <Link to="/login" className="login-btn">Login</Link>
            )}
          </div>

          {/* Mobile icon strip — visible only on ≤768px */}
          {currentUser && userData && (
            <div className="mobile-icons">
              <Link to="/dashboard/meet" className="mobile-icon-btn" title="PrrepX Meet" onClick={() => setMobileMenuOpen(false)}>
                <FaVideo size={18} />
              </Link>
              <Link to="/dashboard/calendar" className="mobile-icon-btn" title="Calendar" onClick={() => setMobileMenuOpen(false)}>
                <FaCalendarAlt size={18} />
              </Link>
              <NotificationBell />
            </div>
          )}

          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-tooltip="Toggle Menu">
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {currentUser && userData ? (
              <ul>{userMenuContent}</ul>
            ) : (
              <Link to="/login" className="login-btn" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;